import {defaultAllowList} from './defaultAllowList';
import {defaultQueryParamsAllowList} from './defaultQueryParamsAllowList';
import {redactPII} from '../redactPII';

export type GoogleAnalytics = (
  ...args: Array<unknown>
) => void | null | undefined;

export const PARAM_REPLACE_TEXT = '[REDACTED]';

const DEFAULT_ALLOWED_PARAMS_SET = new Set(
  Object.keys(defaultQueryParamsAllowList).map((param) => param.toLowerCase()),
);

const createAllowedParamsSet = (
  customAllowList?: Record<string, true>,
): Set<string> => {
  if (!customAllowList || Object.keys(customAllowList).length === 0) {
    return DEFAULT_ALLOWED_PARAMS_SET;
  }

  const allowSet = new Set(DEFAULT_ALLOWED_PARAMS_SET);
  Object.keys(customAllowList).forEach((queryParam) => {
    allowSet.add(queryParam.toLowerCase());
  });

  return allowSet;
};

const pagePathScrubber = (
  pagePath: string,
  customAllowList?: Record<string, true>,
): string => {
  const removeNonAllowlistedParams = (query: string): string => {
    if (query === '') return '';

    const allowSet = createAllowedParamsSet(customAllowList);

    const params = query.split(/[&;]/);
    const allowed: string[] = [];

    params.forEach((originalParam) => {
      const equalIndex = originalParam.indexOf('=');
      const hasValue = equalIndex > -1;
      const name = hasValue
        ? originalParam.substring(0, equalIndex)
        : originalParam;

      if (allowSet.has(name.toLowerCase())) {
        allowed.push(originalParam);
      } else if (hasValue) {
        allowed.push(`${name}=${PARAM_REPLACE_TEXT}`);
      } else {
        allowed.push(name);
      }
    });

    return allowed.length > 0 ? `?${allowed.join('&')}` : '';
  };

  let cleanedPagePath = pagePath;
  const urlSplit = cleanedPagePath.split('?');
  const baseUrl = urlSplit[0];
  const queryParams =
    urlSplit.length > 1
      ? cleanedPagePath.replace(baseUrl, '').substring(1)
      : '';

  const cleanedQueryParams = removeNonAllowlistedParams(queryParams);

  cleanedPagePath = baseUrl + cleanedQueryParams;
  cleanedPagePath = redactPII(cleanedPagePath);

  return cleanedPagePath;
};

export const cleanPagePath = <T extends string | null | undefined>(
  pagePath: T,
  customAllowList?: Record<string, true>,
): T => {
  return pagePath == null
    ? pagePath
    : (pagePathScrubber(pagePath, customAllowList) as T);
};

// Accepts full URL or URI stem and returns same with cleaned query string and/or hash identifier
export const setupGoogleAnalytics = (
  ga: GoogleAnalytics,
  customAllowList?: Record<string, true>,
): void => {
  // There is no reason this should
  // ever be false, but we want to be
  // *extra* safe during the init process
  if (ga) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ga((tracker: any) => {
      // Again, this should never be false
      // but better to be safe
      if (tracker) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tracker.set('customTask', function (model: any) {
          // Gets fieldname values
          const location = model.get('location');
          const pagePath = model.get('page') || undefined;
          const referrer = model.get('referrer') || undefined;

          // Sets fieldnames to PII scrubbed field
          model.set('location', cleanPagePath(location, customAllowList));
          model.set('page', cleanPagePath(pagePath, customAllowList));
          model.set('referrer', cleanPagePath(referrer, customAllowList));
        });
      }
    });
  }
};

const redactPIIFromAttribute = (
  attribute: unknown,
  visited: WeakSet<object | unknown[]> = new WeakSet(),
): unknown => {
  if (typeof attribute === 'string') {
    return redactPII(attribute);
  }

  if (Array.isArray(attribute)) {
    if (visited.has(attribute)) {
      return '[Circular]';
    }
    visited.add(attribute);

    return attribute.map((item) => redactPIIFromAttribute(item, visited));
  }

  if (attribute && typeof attribute === 'object') {
    if (visited.has(attribute)) {
      return '[Circular]';
    }
    visited.add(attribute);

    const objectParam = attribute as Record<string, unknown>;
    Object.keys(objectParam).forEach((objectAttribute) => {
      objectParam[objectAttribute] = redactPIIFromAttribute(
        objectParam[objectAttribute],
        visited,
      );
    });
  }

  return attribute;
};

// This redacts the value for any key that isn't explicitly allow-listed
// and applies redactPII to the allowed values
export const cleanValues = (
  params: {
    [key: string]: unknown;
  },
  allowList?: {
    [key: string]: boolean;
  },
): void => {
  for (const k in params) {
    if (
      params.hasOwnProperty(k) &&
      !(defaultAllowList as Record<string, boolean>)[k] &&
      (!allowList || !allowList[k])
    ) {
      params[k] = PARAM_REPLACE_TEXT;
    } else {
      params[k] = redactPIIFromAttribute(params[k]);
    }
  }
};
